import { useInfiniteQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useMemo } from 'react';
import { LocationMovementsApiAdapter } from '../adapters/LocationMovementsApiAdapter';
import { Filters } from '../models/Filters';
import { Service } from '../services/Service';

export const useLocationMovements = (filters: Filters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new LocationMovementsApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  // 🎯 Serializar filtros de forma estable para la queryKey
  // Esto evita que cambios en la referencia del objeto causen resets innecesarios
  const stableFiltersKey = useMemo(() => {
    // Función recursiva para normalizar objetos y arrays
    const normalizeValue = (value: any): any => {
      if (value === null || value === undefined) {
        return undefined; // No incluir null/undefined
      }
      
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return undefined; // No incluir arrays vacíos
        }
        // Normalizar cada elemento del array
        return value.map(normalizeValue).filter((v) => v !== undefined);
      }
      
      if (typeof value === 'object') {
        const keys = Object.keys(value).sort();
        if (keys.length === 0) {
          return undefined; // No incluir objetos vacíos
        }
        // Normalizar objetos recursivamente
        const normalized: Record<string, any> = {};
        keys.forEach((key) => {
          const normalizedValue = normalizeValue(value[key]);
          if (normalizedValue !== undefined) {
            normalized[key] = normalizedValue;
          }
        });
        return Object.keys(normalized).length > 0 ? normalized : undefined;
      }
      
      return value;
    };
    
    // Normalizar el objeto de filtros
    const normalizedFilters = normalizeValue(filters);
    
    // Si no hay filtros válidos, usar string vacío
    if (!normalizedFilters || Object.keys(normalizedFilters).length === 0) {
      return '';
    }
    
    return JSON.stringify(normalizedFilters);
  }, [filters]);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQuery({
    queryKey: ['location-movements', stableFiltersKey],
    queryFn: ({ pageParam = 1 }) =>
      service.getPaginated({
        ...filters,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    // 🎯 Solo usar placeholderData si hay datos previos válidos
    placeholderData: (previousData) => {
      // Solo mantener datos previos si existen páginas con datos
      if (previousData && previousData.pages && previousData.pages.length > 0) {
        // Verificar que al menos una página tenga datos
        const hasValidData = previousData.pages.some(
          (page) => page?.stockmovements && page.stockmovements.length > 0
        );
        return hasValidData ? previousData : undefined;
      }
      return undefined;
    },
    // 🎯 Mantener datos anteriores mientras se hace refetch
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  const stockmovements =
    data?.pages.flatMap((page) => page?.stockmovements ?? []) ?? [];

  // Obtener el total del API (siempre sera el mismo en todas las paginas)
  const totalFromApi = data?.pages?.[0]?.total ?? 0;

  return {
    stockmovements,
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    // Nuevas props para scroll infinito
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    // Stats útiles
    totalPages: data?.pages.length ?? 0,
    totalLocations: stockmovements.length,
    total: totalFromApi, // Total real del API
  };
};
